import React from "react";

/**
 * Utility comuni per la sezione "Dati societari" (Phase 9).
 *
 * `sito_web`: valore stringa già mostrato come testo; l'href aggiunge
 * automaticamente `https://` se il valore non inizia già con `http://` o
 * `https://` (best-effort UX, non validation).
 *
 * `cellulari`: array di stringhe (già filtrate lato backend).
 */

export function normalizeUrl(raw) {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    if (/^https?:\/\//i.test(s)) return s;
    return `https://${s}`;
}

export function SitoWebLink({ value, testid }) {
    if (!value) return <span>—</span>;
    const href = normalizeUrl(value);
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="lt-link"
            data-testid={testid || "sito-web-link"}
            style={{ wordBreak: "break-all" }}
        >
            {value}
        </a>
    );
}

export function CellulariList({ items, testid }) {
    const list = (items || []).filter(Boolean);
    if (list.length === 0) return <span>—</span>;
    return (
        <ul style={{ margin: 0, padding: 0, listStyle: "none" }} data-testid={testid || "cellulari-list"}>
            {list.map((cel, idx) => {
                const cleaned = String(cel).replace(/\s+/g, "");
                return (
                    <li key={`${cleaned}-${idx}`} style={{ padding: "2px 0" }}>
                        <a href={`tel:${cleaned}`} className="lt-link" data-testid={`cel-link-${idx}`}>
                            {cel}
                        </a>
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * Repeatable input array per cellulari. Rende sempre almeno 2 righe.
 * Il chiamante gestisce lo stato come array di stringhe.
 */
export function CellulariEditor({ values, onChange, testid }) {
    // Garantisce sempre 2 righe minime a UI. Il write finale filtra i vuoti.
    const rows = values.length < 2 ? [...values, ...Array(2 - values.length).fill("")] : values;

    const setAt = (idx, v) => {
        const next = [...rows];
        next[idx] = v;
        onChange(next);
    };
    const removeAt = (idx) => {
        if (rows.length <= 1) return;
        const next = rows.filter((_, i) => i !== idx);
        onChange(next);
    };
    const addRow = () => onChange([...rows, ""]);

    return (
        <div data-testid={testid || "cellulari-editor"}>
            {rows.map((v, idx) => (
                <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "stretch" }}>
                    <input
                        className="lt-field"
                        style={{ flex: 1, margin: 0 }}
                        value={v}
                        onChange={(e) => setAt(idx, e.target.value)}
                        placeholder="+39 340 1234567"
                        data-testid={`cel-input-${idx}`}
                    />
                    <button
                        type="button"
                        onClick={() => removeAt(idx)}
                        disabled={rows.length <= 1}
                        className="lt-btn-ghost"
                        aria-label="Rimuovi numero"
                        title="Rimuovi"
                        data-testid={`cel-remove-${idx}`}
                        style={{
                            padding: "6px 10px", minWidth: 36,
                            opacity: rows.length <= 1 ? 0.35 : 1,
                            cursor: rows.length <= 1 ? "not-allowed" : "pointer",
                        }}
                    >
                        ×
                    </button>
                </div>
            ))}
            <button type="button" onClick={addRow} className="lt-btn-ghost" data-testid="cel-add" style={{ padding: "4px 8px" }}>
                + Aggiungi numero
            </button>
        </div>
    );
}
